import api from "../services/index";

// Every resignation endpoint touches the same single record per employee, so one
// tag is enough: any successful mutation refetches both the employee's own view
// and the admin queue.
const resignationApi = api
  .enhanceEndpoints({ addTagTypes: ["Resignation"] })
  .injectEndpoints({
    endpoints: (builder) => ({
      /* ---------------- Employee side ---------------- */

      // Current logged-in employee's resignation record (HLD 4.1)
      getMyResignationStatus: builder.query({
        query: () => ({
          url: "/api/resignation/status",
          method: "GET",
        }),
        providesTags: ["Resignation"],
      }),

      // IDLE -> PENDING (HLD 3.1)
      submitResignation: builder.mutation({
        query: ({ employeeId, ...body }) => ({
          url: `/api/resignation/${employeeId}/submit`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["Resignation"],
      }),

      // PENDING -> IDLE (HLD 3.2)
      withdrawResignation: builder.mutation({
        query: ({ employeeId }) => ({
          url: `/api/resignation/${employeeId}/withdraw`,
          method: "POST",
        }),
        invalidatesTags: ["Resignation"],
      }),

      // APPROVED -> UPLOADED (HLD 3.5). Files are pushed to S3 first, this call
      // only persists the resulting { fileName, fileUrl } metadata.
      uploadResignationDocuments: builder.mutation({
        query: (body) => ({
          url: "/api/resignation/uploadDocuments",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Resignation"],
      }),

      /* ---------------- Admin side ---------------- */

      // Queue of every resignation record (HLD 5.1)
      getResignationList: builder.query({
        query: (status) => ({
          url: "/api/resignation",
          method: "GET",
          ...(status && status !== "ALL" ? { params: { status } } : {}),
        }),
        providesTags: ["Resignation"],
      }),

      // Full detail for one employee (HLD 5.2)
      getResignationByEmployee: builder.query({
        query: (employeeId) => ({
          url: `/api/resignation/${employeeId}`,
          method: "GET",
        }),
        providesTags: ["Resignation"],
      }),

      // PENDING -> APPROVED | IDLE (HLD 3.3 / 3.4)
      // `lastWorkingDay` is a plain YYYY-MM-DD date the reviewer confirms in the
      // approve dialog (approval date + the employee's notice period, editable).
      // It is sent only with APPROVE and must win over any value the backend
      // derives for itself.
      reviewResignation: builder.mutation({
        query: ({ employeeId, decision, rejectionReason, lastWorkingDay }) => ({
          url: `/api/resignation/${employeeId}/review`,
          method: "POST",
          body: {
            decision,
            ...(rejectionReason ? { rejectionReason } : {}),
            ...(lastWorkingDay ? { lastWorkingDay } : {}),
          },
        }),
        invalidatesTags: ["Resignation"],
      }),

      // UPLOADED -> COMPLETED | APPROVED (HLD 3.6 / 3.7)
      reviewResignationDocuments: builder.mutation({
        query: ({ employeeId, decision, rejectionReason }) => ({
          url: `/api/resignation/${employeeId}/reviewDocuments`,
          method: "POST",
          body: { decision, ...(rejectionReason ? { rejectionReason } : {}) },
        }),
        invalidatesTags: ["Resignation"],
      }),

      deactivateEmployee: builder.mutation({
        query: ({ employeeId }) => ({
          url: "/api/employee/deleteById",
          method: "DELETE",
          params: { employeeId },
        }),
        invalidatesTags: ["Resignation"],
      }),
    }),
  });

export default resignationApi;

export const {
  useGetMyResignationStatusQuery,
  useSubmitResignationMutation,
  useWithdrawResignationMutation,
  useUploadResignationDocumentsMutation,
  useGetResignationListQuery,
  useGetResignationByEmployeeQuery,
  useReviewResignationMutation,
  useReviewResignationDocumentsMutation,
  useDeactivateEmployeeMutation,
} = resignationApi;
